import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, ShieldCheck, KeyRound } from "lucide-react";

const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPass, setNewPass] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/api/auth/users");
      setUsers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (username.trim().length < 3) return toast.error("Username must be at least 3 characters");
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    setCreating(true);
    try {
      await api.post("/api/auth/users", { username: username.trim().toLowerCase(), password });
      toast.success("User created");
      setUsername(""); setPassword("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create user");
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.username}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/auth/users/${u.username}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete user");
    }
  };

  const onResetPassword = async (e) => {
    e.preventDefault();
    if (newPass.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.post(`/api/auth/users/${resetFor}/password`, { new_password: newPass });
      toast.success(`Password reset for ${resetFor}`);
      setResetFor(null); setNewPass("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset password");
    }
  };

  const inputCls = "mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-md focus:outline-none focus:border-zinc-900 text-sm";

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-zinc-400" /></div>;

  return (
    <div data-testid="users-page" className="max-w-3xl">
      <h1 className="text-2xl font-medium text-zinc-900">Users</h1>
      <p className="text-sm text-zinc-500 mt-1">Manage admin accounts that can sign in to the CMS.</p>

      {/* Create user */}
      <form onSubmit={onCreate} className="mt-8 bg-white border border-zinc-200 rounded-lg p-6">
        <div className="flex items-center gap-2 text-sm font-medium text-zinc-900 mb-4"><UserPlus size={16} /> Add a new user</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-zinc-600">Username</label>
            <input data-testid="user-create-username" value={username} onChange={(e) => setUsername(e.target.value)} className={inputCls} placeholder="e.g. maria" required />
          </div>
          <div>
            <label className="text-xs text-zinc-600">Password</label>
            <input data-testid="user-create-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} placeholder="min. 6 characters" required />
          </div>
        </div>
        <button data-testid="user-create-btn" type="submit" disabled={creating} className="mt-4 inline-flex items-center gap-2 bg-zinc-900 text-white text-sm px-4 py-2.5 rounded-md hover:bg-zinc-800 disabled:opacity-60">
          {creating ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />} Create user
        </button>
      </form>

      {/* User list */}
      <ul className="mt-8 space-y-3">
        {users.map((u) => (
          <li key={u.username} data-testid={`user-${u.username}`} className="flex items-center justify-between bg-white border border-zinc-200 rounded-lg px-5 py-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-zinc-900">{u.username}</span>
                {u.protected && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"><ShieldCheck size={11} /> Primary</span>}
                {u.username === user?.username && <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-100 border border-zinc-200 px-2 py-0.5 rounded-full">You</span>}
              </div>
              {u.created_at && <p className="text-[11px] text-zinc-400 mt-1">Added {new Date(u.created_at).toLocaleDateString()}</p>}
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => { setResetFor(u.username); setNewPass(""); }} data-testid={`user-reset-${u.username}`} className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900 border border-zinc-300 rounded-md px-2.5 py-1.5">
                <KeyRound size={13} /> Reset password
              </button>
              {!u.protected && u.username !== user?.username && (
                <button onClick={() => onDelete(u)} data-testid={`user-delete-${u.username}`} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {/* Reset password modal */}
      {resetFor && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center px-4" data-testid="user-reset-modal">
          <form onSubmit={onResetPassword} className="bg-white rounded-lg w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-zinc-900"><KeyRound size={16} /> Reset password — {resetFor}</div>
            <input data-testid="user-reset-input" type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} className={inputCls} placeholder="New password (min. 6)" required autoFocus />
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setResetFor(null)} className="text-sm text-zinc-600 hover:text-zinc-900 px-3 py-2">Cancel</button>
              <button type="submit" data-testid="user-reset-confirm" className="text-sm bg-zinc-900 text-white px-4 py-2 rounded-md hover:bg-zinc-800">Save</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Users;
